/**
 * The two headline numbers above the subscription list, and the package strip
 * that doubles as the list's package filter.
 */
import { CreditCard, Users } from "lucide-react";
import { formatMoney, localeText, type Locale, type PlansT, type SubscriptionStats } from "@wa/ui";
import { Card, CardHead, Kpi, PillTabs, fmtNum } from "../../ui";

export function SubsStats({
  stats,
  t,
  locale,
  planFilter,
  onPlanFilter,
}: {
  stats: SubscriptionStats | null;
  t: PlansT;
  locale: Locale;
  /** Package code, or `"all"`. */
  planFilter: string;
  onPlanFilter: (code: string) => void;
}) {
  const planOptions = stats?.byPlan ?? [];

  return (
    <>
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <Kpi
          icon={Users}
          tone="primary"
          label={t("subs.totalSites")}
          value={stats ? fmtNum(stats.total) : "-"}
        />
        {/* The headline is what Stripe actually bills, which is the same figure
            the admin dashboard shows. The catalogue list price appears under it
            only when the two disagree - that gap is a coupon, and hiding it
            would make the discount look like a bug in one of the two screens. */}
        <Kpi
          icon={CreditCard}
          tone="success"
          label={t("subs.mrr")}
          value={stats ? formatMoney(stats.realMrrCents, "EUR", locale) : "-"}
          foot={
            stats && stats.realMrrCents !== stats.mrrCents
              ? `${t("subs.listPrice")}: ${formatMoney(stats.mrrCents, "EUR", locale)}`
              : undefined
          }
        />
      </div>

      {/* EVERY platform package, not only the ones somebody is on. The endpoint
          returns the whole catalogue, so a package added later shows up here on
          its own with a 0 next to it - which is exactly the number worth seeing.
          Clicking one filters the list; clicking it again goes back to all. */}
      {planOptions.length > 0 ? (
        <Card className="mb-4">
          <CardHead title={t("subs.sitesPerPlan")} />
          <div className="px-6 pb-5 pt-3">
            <PillTabs
              small
              value={planFilter}
              onChange={(code) => onPlanFilter(code === planFilter ? "all" : code)}
              tabs={[
                { value: "all", label: t("all"), count: stats?.total },
                ...planOptions.map((p) => ({
                  value: p.planCode,
                  label: localeText(p.planName, locale) || p.planCode,
                  count: p.count,
                })),
              ]}
            />
          </div>
        </Card>
      ) : null}
    </>
  );
}
